import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/articles';

const brandValues = [
  {
    icon: 'ri-shield-check-line',
    title: '专业权威',
    desc: '内容由婚姻法律专家、心理咨询师和调查专业人士共同把关，确保每篇文章的专业性和准确性。',
  },
  {
    icon: 'ri-eye-line',
    title: '合法合规',
    desc: '始终坚持合法取证原则，所有调查方法均在法律框架内，绝不推荐任何违法手段。',
  },
  {
    icon: 'ri-heart-line',
    title: '温暖陪伴',
    desc: '我们理解每一位来访者正在经历的痛苦，以专业的态度提供最温暖的支持和陪伴。',
  },
  {
    icon: 'ri-lock-line',
    title: '隐私保护',
    desc: '严格保护用户隐私，所有咨询和信息交流均采用最高级别的保密措施。',
  },
];

const stats = [
  { value: '50,000+', label: '累计服务用户' },
  { value: '200+', label: '专业文章' },
  { value: '98%', label: '用户满意度' },
  { value: '5年', label: '行业经验' },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u3pf5ca23r0tr1aptg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, String(value)])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (
        response.ok &&
        parsed?.code === 'OK'
      ) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const isSpam =
          typeof serverMsg === 'string' &&
          (serverMsg.includes('spam') || serverMsg.includes('form data is spam'));
        setFormError(
          isSpam ? '提交失败，请稍后重试。' : (typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。')
        );
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查您的网络连接后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Introduction */}
        <section className="py-12 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-16">
              <div className="flex-1">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
                  关于我们
                </h1>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  <strong className="text-foreground-900">奸情调查取证全攻略</strong>成立于2021年，是国内领先的婚姻危机处理与婚外情调查取证专业知识平台。我们汇聚了婚姻法律专家、资深心理咨询师和前执法人员，致力于为面临婚姻危机的用户提供专业、合法、全面的解决方案。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  在多年的从业经历中，我们见证了太多因信息不对称而蒙受损失的案例——有人在没有证据的情况下冲动质问，导致打草惊蛇；有人收集了大量证据却因为方法不当而无法在法庭上使用；也有人在发现真相后陷入严重的心理危机，无法正常生活。
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  我们的使命是帮助每一位来访者，以合法的方式还原真相，以理性的态度应对危机，以坚强的姿态重建生活。无论您最终选择原谅还是离开，我们都希望您是基于充分信息和理性思考做出的决策。
                </p>
              </div>

              <div className="flex-1 w-full">
                <div className="relative w-full h-72 md:h-80 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20professional%20office%20interior%20with%20soft%20natural%20light%2C%20bookshelves%20and%20comfortable%20seating%2C%20trustworthy%20and%20calm%20atmosphere%2C%20editorial%20photography%2C%20warm%20terracotta%20and%20cream%20accents%2C%20harmonious%20composition&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="奸情调查取证全攻略 - 专业办公环境"
                    className="w-full h-full object-cover object-top rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-primary-500">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-background-50 mb-1 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-sm text-background-50/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Values */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                我们的承诺
              </h2>
              <p className="text-foreground-600 text-sm">四大核心价值观，守护您的权益与隐私</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {brandValues.map((value) => (
                <div
                  key={value.title}
                  className="text-center p-6 bg-background-50 border border-background-200/70 rounded-lg"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                    <i className={`${value.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-600 text-sm">来自真实用户的评价与反馈</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                      <span className="text-xs text-foreground-500">{t.location}</span>
                    </div>
                    <span className="text-xs text-foreground-500">{t.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-600 text-sm">
                如果您正在经历婚姻危机，请随时联系我们。所有信息严格保密。
              </p>
            </div>

            <form
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8"
            >
              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-accent-100 border border-accent-200/70 rounded-md">
                  <p className="text-sm text-accent-800 font-medium">
                    提交成功！感谢您的信任，我们会在24小时内与您联系。
                  </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  联系电话
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="您的手机号码（选填）"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="topic" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  咨询主题 <span className="text-primary-500">*</span>
                </label>
                <select
                  id="topic"
                  name="topic"
                  required
                  className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                >
                  <option value="">请选择咨询主题</option>
                  <option value="调查方法">奸情调查方法咨询</option>
                  <option value="证据收集">婚外情证据收集指导</option>
                  <option value="法律维权">法律维权路径咨询</option>
                  <option value="心理疏导">心理疏导与支持</option>
                  <option value="危机处理">婚姻危机综合处理</option>
                  <option value="其他">其他咨询</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  详细描述 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请简要描述您的情况和需要咨询的问题（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
              </div>

              {/* Anti-spam honeypot */}
              <div className="antispam-field">
                <label htmlFor="phone_alt">备用电话</label>
                <input
                  type="text"
                  id="phone_alt"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                提交咨询
              </button>

              <p className="text-xs text-foreground-500 text-center mt-3">
                提交即表示您同意我们的隐私政策，我们承诺严格保护您的个人信息安全。
              </p>
            </form>

            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <div className="text-center p-4 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-mail-line text-secondary-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-600">contact@jianqingdiaocha.com</p>
              </div>
              <div className="text-center p-4 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-phone-line text-secondary-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">咨询热线</h3>
                <p className="text-xs text-foreground-600">400-888-XXXX</p>
              </div>
              <div className="text-center p-4 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-time-line text-secondary-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">服务时间</h3>
                <p className="text-xs text-foreground-600">周一至周日 9:00-21:00</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 奸情调查取证全攻略',
            description:
              '奸情调查取证全攻略是国内领先的婚姻危机处理与婚外情调查取证专业知识平台。汇聚婚姻法律专家、资深心理咨询师，提供专业合法全面的解决方案。',
            isPartOf: {
              '@type': 'WebSite',
              name: '奸情调查取证全攻略',
              url: 'https://marriagelaw.github.io/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://marriagelaw.github.io/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          }),
        }}
      />
    </>
  );
}